import React, { Component } from "react";
import { Button, FormControl, CircularProgress, TextField, Autocomplete, Select, MenuItem, Box, Slide, Typography, Chip, Checkbox } from "@mui/material";
import AlertDialogPopop from "../../../Component/AlertDialogPopop/AlertDialogPopop";
import { ShowAlert } from "../../../Component/ShowAlert";
import HowToRegTwoToneIcon from '@mui/icons-material/HowToRegTwoTone';
import UserAndPermissionService from "../../../Services/UserAndPermissionService";
import CommonPermissonComp from "../../../Component/Permissions/CommonPermissonComp";
import NewUserForm from "../../../Component/AddUser/AddNewUser";
import AddIcon from '@mui/icons-material/Add';
import { TransitionProps } from '@mui/material/transitions';
import { Permissions } from "../../../Component/Permissions/PermissionsInterface.";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { getFXReportDefaultPermissions } from '../../../Component/Reusables/Utility';

interface UserPermissionsDialogProps {
    open: boolean;
    onClose?: (state: boolean, action: string) => void;
    accessToken: string;
    onValidationChange: (hasErrors: boolean) => void;
}

export enum ActionButtons {
    Save = 'save',
    Cancel = 'cancel'
}

interface Role {
    role_id: string;
    role_name: string;
}

interface EmailOption {
    email: string;
    user_id: string;
}

interface UserData {
    user_id: string;
    email: string;
    message: string;
    status: string
}

type SelectedUser = {
    email: string;
    user_id: string;
};

interface UserPermissionsDialogState {
    roleList: Role[];
    selectedRoleName: string;
    selectedRoleID: string;
    permissions: Permissions;
    emailOptions: EmailOption[];
    loading: boolean;
    firstName: string,
    lastName: string,
    email: string,
    firstNameError: string,
    lastNameError: string,
    emailError: string,
    isOpenNewUserForm: boolean,
    userData: UserData | null;
    isUserAndRoleSettingSaved: boolean;
    selectedUsers: SelectedUser[];
    isPermissionLoading: boolean;
}

// Transition prpos
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

class UserAndRoleSettings extends Component<UserPermissionsDialogProps, UserPermissionsDialogState> {

    constructor(props: UserPermissionsDialogProps) {
        super(props);
        this.state = {
            firstName: "",
            lastName: "",
            email: "",
            firstNameError: "",
            lastNameError: "",
            emailError: "",
            roleList: [],
            selectedRoleName: "",
            selectedRoleID: "",
            emailOptions: [] as SelectedUser[],
            loading: false,
            permissions: {
                profileSettings: {
                    isExchangeRateVisible: true,
                    isExchangeRateSaveBtnVisible: true,
                    isGeneralSettingsVisible: true,
                    isGeneralSettingSaveBtnVisible: true,
                    isPauseTransactionVisible: true,
                    isCardVerification: true,
                },
                dashboard: {
                    isVisible: true,
                    isHeaderVisible: true,
                    isTransactionMetricsVisible: true,
                    isFinancialMetricsVisible: true,
                    isRiskMetricsVisible: true,
                    isSupportMetricsVisible: true,
                    isPerformanceMetricsVisible: true,
                },
                transactionLogs: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isFraudMarkVisible: true,
                    isRefundVisible: true
                },
                userLogs: {
                    isVisible: true,
                },
                complianceLogs: {
                    isVisible: true,
                    isZendeskNavigationAllowed: true,
                    isTransactionNavigationAllowed: true
                },
                userDetails: {
                    isVisible: true,
                    isVisibleAdminsTab: true,
                    isVisibleMobileUsersTab: true,
                    isVisibleSupportStaffsTab: true,
                    isBlockUserVisible: true,
                    isUserSettingsVisible: true,
                    isUserSettingSaveBtnVisible: true,
                    isStatusHistoryVisible: true,
                    isWalletHistoryTabVisible: true,
                    isUpdatePermissionVisible: true,
                    isDownloadVisibleAdmins: true,
                    isDownloadVisibleMobileUsers: true,
                    isDownloadVisibleSupportStaffs: true,
                    isEditUserInfoVisible: true,
                    isAddUserVisible: true
                },
                userTransactionReport: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isLimitHistoryVisible: true,
                    isLimitHistoryDownloadVisible: true
                },
                promoCode: {
                    isVisible: true,
                    isDownloadVisiblePromoCode: true,
                    isDownloadVisibleCashbackCoupon: true,
                    isVisiblePromoCodeTab: true,
                    isVisibleCouponCodeTab: true,
                    isAddPromoCodeVisible: true,
                    isAddCouponCodeVisible: true,
                    isBlockCodeVisible: true,
                    isActiveCodeVisible: true
                },
                exchangeRates: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isAddCurrencyPairVisible: true,
                    isEditRatesVisible: true,
                    isEditPaymentMethodVisible: true,
                    isActiveCurrencyAllowed: true,
                    isInactiveCurrencyAllowed: true,
                },
                referrals: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isReferralSettingsVisible: true,
                    isSaveSettingsAllowed: true,
                    isViewRefereesVisible: true,
                    isTransactionNavigationAllowed: true
                },
                chargeBack: {
                    isVisible: true,
                    isDownloadFileAllowed: true,
                    isSendEmailAllowd: true,
                },
                partner: {
                    isVisible: true,
                    isVisibleLiquidityTab: true,
                    isVisiblePayoutTab: true,
                    isDownloadVisibleLiquidity: true,
                    isDownloadVisiblePayout: true,
                    isAddVisibleLiquidity: true,
                    isAddVisiblePayout: true,
                    isEditPartnersVisible: true,
                    isActivePartnersVisible: true,
                    isInActivePartnersVisible: true
                },
                ratereconciliation: {
                    isVisible: true,
                },
                fxReports: getFXReportDefaultPermissions(),
                fxAllocation: {
                    isVisible: true,
                    isCreatePurchaseVisible: true,
                    isCreateLiquidityVisible: true,
                    isCreatePartnerVisible: true
                },
            },
            isOpenNewUserForm: false,
            userData: null,
            isUserAndRoleSettingSaved: false,
            selectedUsers: [] as SelectedUser[],
            isPermissionLoading: false
        }
    }

    // For email search..
    debounceTimeout: ReturnType<typeof setTimeout> | null = null;

    async componentDidMount() {
        this.getUserRoles();
    }

    // To Update parent component when state changes
    componentDidUpdate(prevProps: Readonly<UserPermissionsDialogProps>, prevState: Readonly<UserPermissionsDialogState>) {
        if (prevState !== this.state) {
            this.notifyValidationStatus();
        }
    }

    // To pass the validation status to the parent component
    notifyValidationStatus = () => {
        if (this.props.onValidationChange) {
            this.props.onValidationChange(this.hasErrors());
        }
    };

    hasErrors = (): boolean => {
        const { selectedUsers, selectedRoleName } = this.state;
        const isSaveDisabled = !selectedUsers.length || !selectedRoleName;
        return isSaveDisabled;
    };

    private async getUserRoles() {
        try {
            const response = await UserAndPermissionService.getUserRoles(this.props.accessToken);
            console.log(response)

            if (!response.ok) {
                throw new Error("Failed to fetch user roles");
            }
            const data = await response.json();
            this.setState({
                roleList: data.roleList

            });
        } catch (error) {
            ShowAlert("Error", "Failed to fetch user Roles. Please try again later.", "error");
            console.error("Error fetching user roles:", error);
        }
    }

    private async handleClose(action: string) {
        const { selectedRoleID, selectedUsers } = this.state;

        if (action === ActionButtons.Save && selectedUsers.length) {
            try {
                const userIds = selectedUsers.map(user => user.user_id);
                let postData = {
                    supportStaffAssign: {
                        roleId: selectedRoleID,
                        userId: userIds,
                    },
                };

                const rsp = await UserAndPermissionService.setUsersRole(this.props.accessToken, postData);
                if (rsp.status === 200) {
                    ShowAlert("Success", "Role assigned successfully to selected user!", "success");
                } else {
                    throw new Error();
                }
            } catch (error) {
                console.error("Error updating user Creation:", error);
                ShowAlert("Alert", "Failed to Save Changes. Please try again later.", "error");
            }
        } else if (this.props.onClose) {
            this.props.onClose(false, action);
        }
    }


    handleEmailSearch = (query: string) => {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout); // Clear previous timeout
        }

        this.debounceTimeout = setTimeout(async () => {
            if (!query) return;
            this.setState({ loading: true });

            try {
                const rsp = await UserAndPermissionService.getUserEmail(this.props.accessToken, query);

                if (rsp.ok) {
                    const data = await rsp.json();
                    const filteredEmails = data.emailList.filter((item: EmailOption) =>
                        item.email.toLowerCase().includes(query.toLowerCase())
                    );

                    this.setState({
                        emailOptions: filteredEmails.length > 0 ? filteredEmails : [],
                        loading: false,
                    });
                } else {
                    console.error("Failed to fetch emails:", rsp.statusText);
                    this.setState({ emailOptions: [], loading: false });
                }
            } catch (error) {
                console.error("Error fetching emails:", error);
                this.setState({ emailOptions: [], loading: false });
            }
        }, 300); // Debounce delay: 300ms
    };

    componentWillUnmount() {
        if (this.debounceTimeout) {
            clearTimeout(this.debounceTimeout); // Cleanup on unmount
        }
    }

    handlePermissionsChange = (updatedPermissions: any) => {
        console.log("Updated Permissions:", updatedPermissions);
        this.setState({ permissions: updatedPermissions });
    };

    handleOpenDialog = () => {
        this.setState({ isOpenNewUserForm: true });
    };

    handleCloseDialog = () => {
        this.setState({ isOpenNewUserForm: false });
    };

    // This is for auto select but not used....
    handleFormSubmit = (data: UserData) => {
        console.log("Received Data from NewUserForm:", data);
        this.setState({
            userData: data,
            isOpenNewUserForm: false,
            // selectedUsers: [{
            //     email: data.email,
            //     user_id: data.user_id
            // }]
        });
    };

    handleRoleSelection = async (e: any) => {
        const selectedRole = this.state.roleList.find(role => role.role_name === e.target.value);
        if (!selectedRole) return; // Safety check

        this.setState({
            selectedRoleName: selectedRole.role_name,
            selectedRoleID: selectedRole.role_id,
            isPermissionLoading: true
        });

        try {
            const response = await UserAndPermissionService.getPermissions(this.props.accessToken, selectedRole.role_id);

            if (!response.ok) throw new Error("Failed to fetch role details");
            const data = await response.json();

            this.setState({ isPermissionLoading: false, permissions: data?.permissions, });
        } catch (error) {
            console.error("Error fetching role details:", error);
            this.setState({ isPermissionLoading: false });
        }
    };


    render() {
        const { emailOptions, isPermissionLoading, selectedUsers } = this.state;

        return (
            <Box style={{ display: "flex", gap: "25px" }}>
                <Box style={{ flex: 1 }}>
                    <Box style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
                        <FormControl sx={{ marginTop: "5px" }}>
                            <Button
                                variant="contained"
                                size="large"
                                color="secondary"
                                onClick={() => this.setState({ isOpenNewUserForm: true })}
                            >
                                ADD NEW USER <AddIcon fontSize="small" />
                            </Button>
                        </FormControl>
                        <NewUserForm
                            open={this.state.isOpenNewUserForm}
                            onClose={() => this.setState({ isOpenNewUserForm: false })}
                            onSubmit={this.handleFormSubmit}
                            accessToken={this.props.accessToken}
                        />
                        <Typography sx={{ textAlign: "center", fontWeight: "bold" }}>OR</Typography>
                        <FormControl>
                            <Autocomplete
                                multiple
                                id="email-multi-selector"
                                options={emailOptions}
                                disableCloseOnSelect
                                size="medium"
                                value={selectedUsers}
                                onInputChange={(_, newInputValue) => this.handleEmailSearch(newInputValue)}
                                onChange={(_, newValue) => this.setState({ selectedUsers: newValue })}
                                renderTags={(tagValue, getTagProps) => {
                                    const visibleTags = tagValue.slice(0, 2);
                                    const remainingCount = tagValue.length - visibleTags.length;
                                    return (
                                        <>
                                            {visibleTags.map((option, index) => (
                                                <Chip label={option.email} {...getTagProps({ index })} />
                                            ))}
                                            {remainingCount > 0 && <Chip label={`+${remainingCount} more`} />}
                                        </>
                                    );
                                }}
                                limitTags={2}
                                openOnFocus
                                selectOnFocus
                                getOptionLabel={(option) => option.email}
                                //noOptionsText={"No options"}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        <Checkbox
                                            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                            checkedIcon={<CheckBoxIcon fontSize="small" />}
                                            style={{ marginRight: 5 }}
                                            checked={selected}
                                            color="secondary"
                                        />
                                        {option.email}
                                    </li>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Search by email"
                                        variant="outlined"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {this.state.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </FormControl>
                        <FormControl sx={{ marginTop: "25px" }}>
                            <Select
                                value={this.state.selectedRoleName || ""}
                                displayEmpty
                                renderValue={(selected) => selected || "Select a role"}
                                onChange={this.handleRoleSelection}
                            >
                                {this.state.roleList.map((role) => (
                                    <MenuItem key={role.role_id} value={role.role_name}>
                                        {role.role_name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>
                <Box style={{ flex: 1 }}>
                    <CommonPermissonComp
                        permissions={this.state.permissions}
                        onPermissionsChange={this.handlePermissionsChange}
                        isPermissionLoading={isPermissionLoading}
                        isReadOnly={true}
                    />
                </Box>
                <AlertDialogPopop
                    open={this.state.isUserAndRoleSettingSaved}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        this.setState({
                            isUserAndRoleSettingSaved: false
                        }, () => {
                            if (action_ == "ok") {
                                this.handleClose(ActionButtons.Save);
                            }
                        })
                    }}
                />
            </Box>
        );
    }
}

export default UserAndRoleSettings;